import { Query } from '@cubejs-client/core'
import { API } from '@getgreenline/homi-shared'
import {
  IInventoryOnHandCategoriesContract,
  IInventoryOnHandProductsContract,
  InventoryOnHandGroupBy,
} from '@getgreenline/inventory/build/models/inventory'
import { InventoryAPI } from '@getgreenline/inventory'
import { Track } from '../../../../../../Track'
import { CubeMaxInventoryLogFields } from './enums'
import { generateMaxIdQuery, generateInventoryOnHandQuery } from './queries'
import {
  ICubeProductOnHand,
  ICubeCategoryOnHand,
  IInventoryOnHand,
  ICubeMaxLogIdResponse,
} from './types'
import {
  groupCategoriesOnHand,
  isToday,
  processCategoriesOnHandContract,
  processProductsOnHandContract,
  processSubCategoriesOnHandContract,
} from './utils'
import pRetry, { AbortError } from 'p-retry'

export const runCubeQueryWithRetry = async <T>({
  companyId,
  query,
}: {
  companyId: number
  query: Query
}): Promise<T[]> => {
  const result = await pRetry(
    async () => {
      const res = await API.getQuery(companyId, query)
      if (res.status === 200 && res.data.error) throw new Error(res.data.error)
      if (res.status !== 200) {
        throw new AbortError(res.data.error)
      }
      return res
    },
    { retries: 3, minTimeout: 5000 },
  )
  return result.data.data
}

export const fetchMaxInventoryLogIds = async ({
  companyId,
  query,
}: {
  companyId: number
  query: Query
}): Promise<string[]> => {
  const cubeResults = await runCubeQueryWithRetry<ICubeMaxLogIdResponse>({ companyId, query })
  const result = cubeResults.map(
    (maxLogIds: ICubeMaxLogIdResponse) => `${maxLogIds[CubeMaxInventoryLogFields.MAX_ID]}`,
  )
  return result
}

export const fetchCubeReport = async <CubeReportContract>({
  companyId,
  query,
}: {
  companyId: number
  query: Query
}): Promise<CubeReportContract[]> => {
  Track.startTimer('dashboard:reports_inventoryOnHandHistorical')
  const res = await runCubeQueryWithRetry<CubeReportContract>({ companyId, query })
  Track.track('dashboard:reports_inventoryOnHandHistorical')
  return res
}

export const fetchProductsOnHandCube = async ({
  companyId,
  locationIds,
  startDate,
  includeOutOfStock,
}: {
  companyId: number
  locationIds: number[]
  startDate: Date
  includeOutOfStock?: boolean
}) => {
  const maxIdQuery = generateMaxIdQuery({ companyId, locationIds, startDate })
  const maxInventoryLogIds = await fetchMaxInventoryLogIds({
    companyId,
    query: maxIdQuery,
  })
  if (!maxInventoryLogIds.length)
    throw new Error('No inventory found for the selected date, please try a different date')
  const productsOnHandQuery = generateInventoryOnHandQuery({
    maxInventoryLogIds,
    groupBy: InventoryOnHandGroupBy.PRODUCTS,
    includeOutOfStock,
    limit: 50000,
  })
  const productsOnHand = await fetchCubeReport<ICubeProductOnHand>({
    companyId,
    query: productsOnHandQuery,
  })
  return processProductsOnHandContract(productsOnHand)
}

export const fetchCategoriesOnHandCube = async ({
  companyId,
  locationIds,
  startDate,
  includeSubCategories,
  includeOutOfStock,
}: {
  companyId: number
  locationIds: number[]
  startDate: Date
  includeSubCategories?: boolean
  includeOutOfStock?: boolean
}) => {
  const maxIdQuery = generateMaxIdQuery({ companyId, locationIds, startDate })
  const maxInventoryLogIds = await fetchMaxInventoryLogIds({
    companyId,
    query: maxIdQuery,
  })
  if (!maxInventoryLogIds.length)
    throw new Error('No inventory found for the selected date, please try a different date')
  const categoriesOnHandQuery = generateInventoryOnHandQuery({
    maxInventoryLogIds,
    groupBy: InventoryOnHandGroupBy.CATEGORIES,
    includeOutOfStock,
  })
  const categoriesOnHand = await fetchCubeReport<ICubeCategoryOnHand>({
    companyId,
    query: categoriesOnHandQuery,
  })
  const categoriesOnHandGrouped = groupCategoriesOnHand(
    categoriesOnHand,
    includeSubCategories === true,
  )
  return includeSubCategories
    ? processSubCategoriesOnHandContract(categoriesOnHandGrouped)
    : processCategoriesOnHandContract(categoriesOnHandGrouped)
}

export const fetchInventoryOnHandToday = async ({
  companyId,
  groupBy,
  locationIds,
  includeSubCategories,
  includeOutOfStock,
}: {
  companyId: number
  groupBy: InventoryOnHandGroupBy
  locationIds: number[]
  includeSubCategories?: boolean
  includeOutOfStock?: boolean
}) => {
  Track.startTimer('dashboard:reports_inventoryOnHand')
  const inventoryOnHandRaw = await InventoryAPI.getInventoryOnHand(companyId, {
    filter: {
      groupBy,
      locationIds,
      includeSubCategories: includeSubCategories === true,
      includeOutOfStock: includeOutOfStock === true,
      limit: 10000,
      offset: 0,
    },
    responseType: 'preview',
  })
  Track.track('dashboard:reports_inventoryOnHand')
  if (groupBy === InventoryOnHandGroupBy.CATEGORIES && includeSubCategories) {
    return processSubCategoriesOnHandContract(
      inventoryOnHandRaw as IInventoryOnHandCategoriesContract[],
    )
  }
  if (groupBy === InventoryOnHandGroupBy.CATEGORIES) {
    return processCategoriesOnHandContract(
      inventoryOnHandRaw as IInventoryOnHandCategoriesContract[],
    )
  }
  if (groupBy === InventoryOnHandGroupBy.PRODUCTS) {
    return processProductsOnHandContract(inventoryOnHandRaw as IInventoryOnHandProductsContract[])
  }
  throw new Error('Invalid groupBy specified')
}

export const fetchInventoryOnHand = ({
  groupBy,
  companyId,
  locationIds,
  startDate,
  includeSubCategories,
  includeOutOfStock,
}: {
  groupBy: InventoryOnHandGroupBy
  companyId: number
  locationIds: number[]
  startDate: Date
  includeSubCategories?: boolean
  includeOutOfStock?: boolean
}): Promise<IInventoryOnHand[]> => {
  if (isToday(startDate))
    return fetchInventoryOnHandToday({
      companyId,
      groupBy,
      locationIds,
      includeSubCategories,
      includeOutOfStock,
    })
  if (groupBy === InventoryOnHandGroupBy.CATEGORIES) {
    return fetchCategoriesOnHandCube({
      companyId,
      locationIds,
      startDate,
      includeOutOfStock,
      includeSubCategories,
    })
  }
  if (groupBy === InventoryOnHandGroupBy.PRODUCTS) {
    return fetchProductsOnHandCube({
      companyId,
      locationIds,
      startDate,
      includeOutOfStock,
    })
  }
  throw new Error('Invalid groupBy specified')
}
